import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

/**
 * Keeps Mission Control (and the case full-map view) alive while the user
 * moves between other pages of the same case, so the map is not rebuilt on return.
 * Does not inject Router: RouteReuseStrategy is created by the router.
 */
@Injectable()
export class CaseDashboardReuseStrategy implements RouteReuseStrategy {
  private readonly stored = new Map<string, DetachedRouteHandle>();
  /** Case id of the navigation in progress. Null when leaving the case area. */
  private upcomingCaseId: string | null = null;

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    const key = this.storageKey(route);
    return !!key && this.caseIdOf(route) === this.upcomingCaseId;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.storageKey(route);
    if (!key) {
      return;
    }
    // The router passes null after it has taken the handle back to attach it.
    if (!handle) {
      this.stored.delete(key);
      return;
    }
    if (this.caseIdOf(route) === this.upcomingCaseId) {
      const existing = this.stored.get(key);
      if (existing && existing !== handle) {
        this.destroyHandle(existing);
      }
      // Skip change detection while the page is off-screen so putting it back is instant.
      this.componentRef(handle)?.changeDetectorRef.detach();
      this.stored.set(key, handle);
      return;
    }
    this.destroyHandle(handle);
    this.destroyHandle(this.stored.get(key));
    this.stored.delete(key);
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.storageKey(route);
    return !!key && this.stored.has(key) && this.caseIdOf(route) === this.upcomingCaseId;
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.storageKey(route);
    if (!key) {
      return null;
    }
    const handle = this.stored.get(key) || null;
    if (handle) {
      this.reattachAfterPaint(handle);
    }
    return handle;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    this.noteUpcomingCase(future);
    return future.routeConfig === curr.routeConfig;
  }

  /**
   * Called once per route level, parent before child. Only a leaf with no case id
   * means the navigation is leaving the case.
   */
  private noteUpcomingCase(route: ActivatedRouteSnapshot): void {
    const caseId = this.caseIdFromTree(route);
    if (caseId) {
      if (caseId !== this.upcomingCaseId) {
        this.upcomingCaseId = caseId;
        this.clearExcept(caseId);
      }
      return;
    }
    if (!route.firstChild) {
      this.upcomingCaseId = null;
      this.clearExcept(null);
    }
  }

  private caseIdFromTree(route: ActivatedRouteSnapshot): string | null {
    for (const snap of route.pathFromRoot || [route]) {
      const caseId = this.caseIdOf(snap);
      if (caseId) {
        return caseId;
      }
    }
    return this.caseIdOf(route);
  }

  private storageKey(route: ActivatedRouteSnapshot): string | null {
    const path = route.routeConfig?.path;
    // Timeline's DOM is large enough that destroying it blocks the switch back.
    if (path !== '' && path !== 'map' && path !== 'timeline' && path !== 'timeline/:timelineid') {
      return null;
    }
    const caseId = this.caseIdOf(route);
    if (!caseId || !this.isUnderCase(route)) {
      return null;
    }
    const timelineId = path === 'timeline/:timelineid' ? `|${route.params?.['timelineid'] || ''}` : '';
    return `case-dashboard|${caseId}|${path}${timelineId}`;
  }

  private caseIdOf(route: ActivatedRouteSnapshot): string | null {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      if (current.routeConfig?.path === ':id' && current.parent?.routeConfig?.path === 'case' && current.params?.['id']) {
        return String(current.params['id']);
      }
      current = current.parent;
    }
    return null;
  }

  private isUnderCase(route: ActivatedRouteSnapshot): boolean {
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      if (current.routeConfig?.path === 'case') {
        return true;
      }
      current = current.parent;
    }
    return false;
  }

  private clearExcept(caseId: string | null): void {
    const prefix = caseId ? `case-dashboard|${caseId}|` : null;
    for (const key of [...this.stored.keys()]) {
      if (!prefix || !key.startsWith(prefix)) {
        this.destroyHandle(this.stored.get(key));
        this.stored.delete(key);
      }
    }
  }

  private componentRef(handle: DetachedRouteHandle | null | undefined): {
    destroy: () => void;
    changeDetectorRef: { detach: () => void; reattach: () => void; detectChanges: () => void };
  } | null {
    return (handle as { componentRef?: {
      destroy: () => void;
      changeDetectorRef: { detach: () => void; reattach: () => void; detectChanges: () => void };
    } } | null)?.componentRef || null;
  }

  /** Let the cached view appear, then refresh bindings on the next turn. */
  private reattachAfterPaint(handle: DetachedRouteHandle): void {
    const componentRef = this.componentRef(handle) as { __missionControlReattach?: boolean; changeDetectorRef: { reattach: () => void; detectChanges: () => void } } | null;
    if (!componentRef || componentRef.__missionControlReattach) {
      return;
    }
    componentRef.__missionControlReattach = true;
    // Paint the cached page before change detection. The timeline page pushes
    // hundreds of notes into Mission Control, and rebuilding that list in the
    // same turn is what delays this switch compared with other case pages.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        componentRef.__missionControlReattach = false;
        componentRef.changeDetectorRef.reattach();
        componentRef.changeDetectorRef.detectChanges();
      });
    });
  }

  private destroyHandle(handle: DetachedRouteHandle | undefined | null): void {
    this.componentRef(handle)?.destroy();
  }
}
